import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MemberHeader } from '../common/MemberHeader';
import { MemberBreadcrumb } from '../common/MemberBreadcrumb';
import { LicenseCard } from '../common/LicenseCard';
import { LicensesGroupedList } from '../common/LicensesGroupedList';
import PdfDropzone from '../common/PdfDropzone';
import { trainingApi, renewalApi, licenseApi, settingsApi, bookingApi, getBackendUrl } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
    Shield, FileText, HeartPulse, Plus, Trash2, Send, Loader2, ChevronLeft, Upload, AlertCircle, Save, CheckCircle2, Layers, ChevronDown, ChevronUp, X as XIcon, Download,
} from 'lucide-react';

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        let d;
        if (dateStr.includes('.')) {
            const parts = dateStr.split('.');
            if (parts.length === 3) d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        } else {
            d = new Date(dateStr + 'T00:00:00');
        }
        if (!d || isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
}

// Dropbox-/Windows-verbotene Zeichen in Dateinamen:  / \ < > : " | ? *
// Wenn einer davon im Namen steckt, schlägt die Archivierung fehl — wir
// weisen den Nutzer daher schon vor dem Upload darauf hin.
const DROPBOX_FORBIDDEN_FILENAME_CHARS = /[\\/:<>"|?*]/g;

function validateFilenameForArchive(filename) {
    if (!filename) return '';
    const matches = filename.match(DROPBOX_FORBIDDEN_FILENAME_CHARS);
    if (!matches) return '';
    const unique = [...new Set(matches)].join(' ');
    return `Der Dateiname enthält verbotene Zeichen (${unique}). Bitte benennen Sie die Datei um und verwenden Sie keine der folgenden Zeichen: / \\ : < > " | ? *`;
}

export default function MyLicenseRenewalPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [trainingRecords, setTrainingRecords] = useState([]);
    const [renewals, setRenewals] = useState([]);
    const [requirements, setRequirements] = useState(null);
    const [licenses, setLicenses] = useState([]);
    const [licenseTypeOptions, setLicenseTypeOptions] = useState([]);

    const [newRecord, setNewRecord] = useState({ name: '', date_issued: '', ue_count: '', record_type: 'regular' });
    const [newFirstAid, setNewFirstAid] = useState({ name: 'Erste-Hilfe mit Herz-Lungen-Wiederbelebung', date_issued: '', ue_count: '', record_type: 'first_aid' });
    const [recordFile, setRecordFile] = useState(null);
    const [firstAidFile, setFirstAidFile] = useState(null);
    const [recordFileError, setRecordFileError] = useState('');
    const [firstAidFileError, setFirstAidFileError] = useState('');
    const [addingRecord, setAddingRecord] = useState(false);

    // Stapel-Upload für mehrere Fortbildungs-Nachweise auf einmal
    const [batchOpen, setBatchOpen] = useState(false);
    const [batchRecords, setBatchRecords] = useState([]);
    const [batchSubmitting, setBatchSubmitting] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });

    const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
    const [renewalSubmitting, setRenewalSubmitting] = useState(false);
    const [renewalForm, setRenewalForm] = useState({
        notes: '', license_number: '', issued_date: '', valid_until: '', file: null,
    });

    const [uploadLicenseOpen, setUploadLicenseOpen] = useState(false);
    const [uploadLicenseForm, setUploadLicenseForm] = useState({
        license_number: '', license_type: '',
        issued_date: '', original_issued_date: '', valid_until: '', file: null,
    });
    const [uploadingLicense, setUploadingLicense] = useState(false);

    const profileIncomplete = !user?.birth_date || !user?.street || !user?.postal_code || !user?.city;

    useEffect(() => {
        loadData();
        settingsApi.getLicenseTypes()
            .then((r) => {
                const opts = r.data.options || [];
                setLicenseTypeOptions(opts);
                if (opts.length) {
                    setUploadLicenseForm((prev) => prev.license_type ? prev : { ...prev, license_type: opts[0] });
                }
            })
            .catch(() => setLicenseTypeOptions([]));
        // Roten Navigations-Badge „Meine Lizenz verlängern" ausblenden, sobald
        // der Nutzer die Seite einmal besucht hat. Das setzt `seen_at` für alle
        // genehmigten Verlängerungsanträge serverseitig.
        renewalApi.markMyRenewalsSeen().catch(() => {});
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [recordsRes, renewalsRes, reqRes, licensesRes] = await Promise.all([
                trainingApi.getRecords(),
                renewalApi.getMyRenewals(),
                renewalApi.getMyRequirements().catch(() => ({ data: null })),
                licenseApi.getMy(),
            ]);
            setTrainingRecords(recordsRes.data);
            setRenewals(renewalsRes.data);
            setRequirements(reqRes.data);
            setLicenses(licensesRes.data);
        } catch (e) {
            console.error('Fehler beim Laden:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSelfUploadLicense = async () => {
        const f = uploadLicenseForm;
        if (!f.license_number || !f.license_type || !f.issued_date || !f.valid_until || !f.file) {
            toast.error('Bitte Lizenznummer, Lizenztyp, Ausstellungsdatum, Gültig bis und PDF angeben.');
            return;
        }
        if (f.file) {
            const err = validateFilenameForArchive(f.file.name);
            if (err) {
                toast.error(err);
                return;
            }
        }
        setUploadingLicense(true);
        try {
            await licenseApi.selfUpload({
                license_number: f.license_number,
                issued_date: f.issued_date,
                valid_until: f.valid_until,
                license_type: f.license_type,
                original_issued_date: f.original_issued_date,
            }, f.file);
            toast.success('Lizenz gespeichert. Die Geschäftsstelle wird diese noch prüfen.');
            setUploadLicenseOpen(false);
            setUploadLicenseForm({
                license_number: '', license_type: licenseTypeOptions[0] || '',
                issued_date: '', original_issued_date: '', valid_until: '', file: null,
            });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
        } finally {
            setUploadingLicense(false);
        }
    };

    const handleAddRecord = async (data, file) => {
        if (!data.name || !data.date_issued || !data.ue_count) {
            toast.error('Bitte alle Felder ausfüllen');
            return;
        }
        if (file && !file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Nur PDF-Dokumente sind erlaubt');
            return;
        }
        if (file) {
            const err = validateFilenameForArchive(file.name);
            if (err) {
                toast.error(err);
                return;
            }
        }
        setAddingRecord(true);
        try {
            await trainingApi.addRecord({ ...data, ue_count: parseInt(data.ue_count) || 0 }, file || null);
            toast.success('Nachweis hinzugefügt');
            setNewRecord({ name: '', date_issued: '', ue_count: '', record_type: 'regular' });
            setNewFirstAid({ name: 'Erste-Hilfe mit Herz-Lungen-Wiederbelebung', date_issued: '', ue_count: '', record_type: 'first_aid' });
            setRecordFile(null);
            setFirstAidFile(null);
            setRecordFileError('');
            setFirstAidFileError('');
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setAddingRecord(false);
        }
    };

    const handleAddBatchFiles = (files) => {
        const today = new Date().toISOString().slice(0, 10);
        const rows = files.map((f) => {
            const baseName = f.name.replace(/\.pdf$/i, '').trim();
            return {
                id: Math.random().toString(36).slice(2),
                file: f,
                name: baseName || 'Fortbildung',
                date_issued: today,
                ue_count: '',
                error: validateFilenameForArchive(f.name) || '',
                status: 'pending',
            };
        });
        setBatchRecords((prev) => [...prev, ...rows]);
    };

    const updateBatchRow = (id, patch) => {
        setBatchRecords((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
    };

    const removeBatchRow = (id) => {
        setBatchRecords((prev) => prev.filter((r) => r.id !== id));
    };

    const handleSubmitBatch = async () => {
        const valid = batchRecords.filter((r) => r.status !== 'done');
        if (valid.length === 0) return;
        for (const r of valid) {
            if (!r.name || !r.date_issued || !r.ue_count) {
                toast.error(`Bitte alle Felder ausfüllen (Datei „${r.file.name}")`);
                return;
            }
            if (r.error) {
                toast.error(`Dateiname „${r.file.name}" entspricht nicht dem Schema (Last, First — Datum)`);
                return;
            }
        }
        setBatchSubmitting(true);
        setBatchProgress({ done: 0, total: valid.length });
        let successes = 0;
        for (let i = 0; i < valid.length; i += 1) {
            const r = valid[i];
            try {
                await trainingApi.addRecord({
                    name: r.name,
                    date_issued: r.date_issued,
                    ue_count: parseInt(r.ue_count) || 0,
                    record_type: 'regular',
                }, r.file);
                updateBatchRow(r.id, { status: 'done' });
                successes += 1;
            } catch (e) {
                updateBatchRow(r.id, { status: 'error', errorMsg: e.response?.data?.detail || 'Fehler' });
            }
            setBatchProgress({ done: i + 1, total: valid.length });
        }
        setBatchSubmitting(false);
        if (successes > 0) {
            toast.success(`${successes} von ${valid.length} Nachweisen hinzugefügt`);
            loadData();
            setBatchRecords((prev) => prev.filter((r) => r.status !== 'done'));
            if (successes === valid.length) {
                setBatchOpen(false);
            }
        } else {
            toast.error('Keiner der Nachweise konnte gespeichert werden');
        }
    };

    const handleDeleteRecord = async (id) => {
        try {
            await trainingApi.deleteRecord(id);
            toast.success('Nachweis gelöscht');
            setTrainingRecords(trainingRecords.filter(r => r.id !== id));
        } catch {
            toast.error('Fehler beim Löschen');
        }
    };

    // PDF eines Fortbildungsnachweises herunterladen.
    //   • Bei extern hochgeladenen Records öffnet ein Token-gesicherter Link
    //     das gespeicherte Dokument direkt (Browser respektiert den
    //     Content-Disposition-Dateinamen — Blob-URLs würden eine UUID zeigen).
    //   • Bei auto-erstellten Records (Aus BGPR-Seminar) wird das PDF
    //     on-demand über die Buchung regeneriert.
    const handleDownloadRecord = async (r) => {
        try {
            if (r.document_path) {
                const token = localStorage.getItem('bgpr_token');
                const url = `${getBackendUrl()}/api/training-records/${r.id}/document?token=${encodeURIComponent(token)}`;
                window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }
            if (!r.booking_id) {
                toast.error('Kein Dokument verfügbar');
                return;
            }
            const res = await bookingApi.downloadCertificate(r.booking_id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const cd = res.headers?.['content-disposition'] || '';
            const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
            const filename = m && m[1]
                ? decodeURIComponent(m[1])
                : `Bescheinigung_${r.booking_id.slice(0, 8)}.pdf`;
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); document.body.removeChild(a);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bescheinigung konnte nicht geladen werden');
        }
    };

    const handleOpenRenewal = () => {
        setRenewalForm({ notes: '', license_number: '', issued_date: '', valid_until: '', file: null });
        setRenewalDialogOpen(true);
    };

    const handleSubmitRenewal = async () => {
        setRenewalSubmitting(true);
        try {
            const payload = { notes: renewalForm.notes || '' };
            if (requirements?.needs_existing_license_upload) {
                if (!renewalForm.license_number || !renewalForm.issued_date || !renewalForm.valid_until || !renewalForm.file) {
                    toast.error('Bitte alle Felder der bestehenden Lizenz ausfüllen und PDF hochladen.');
                    setRenewalSubmitting(false);
                    return;
                }
                const up = await renewalApi.uploadExistingLicense(renewalForm.file);
                payload.existing_license_number = renewalForm.license_number;
                payload.existing_issued_date = renewalForm.issued_date;
                payload.existing_valid_until = renewalForm.valid_until;
                payload.existing_document_path = up.data.document_path;
                payload.existing_document_filename = up.data.filename;
            }
            const res = await renewalApi.requestRenewal(payload);
            // Optimistic UI update — make the "Antrag eingereicht"-State sofort
            // sichtbar, ohne auf den loadData()-Roundtrip zu warten. Dadurch
            // verschwindet der Beantragen-Button unmittelbar nach dem Klick.
            const newRenewal = res?.data && res.data.id
                ? res.data
                : { id: `temp-${Date.now()}`, status: 'pending', created_at: new Date().toISOString() };
            setRenewals(prev => [newRenewal, ...prev]);
            toast.success('Antrag auf Lizenzverlängerung wurde eingereicht');
            setRenewalDialogOpen(false);
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Einreichen');
        } finally {
            setRenewalSubmitting(false);
        }
    };

    const regularRecords = trainingRecords.filter(r => r.record_type !== 'first_aid');
    const firstAidRecords = trainingRecords.filter(r => r.record_type === 'first_aid');
    const sortedRecords = [...trainingRecords].sort((a, b) => {
        const da = (a.date_issued || '').slice(0, 10);
        const dbd = (b.date_issued || '').slice(0, 10);
        if (da !== dbd) return dbd.localeCompare(da);
        return (b.created_at || '').localeCompare(a.created_at || '');
    });
    const hasPendingRenewal = renewals.some(r => r.status === 'pending');

    // Bug Teil 55: Bei Admin-Direkt-Verlängerungen wurde `used_for_license_id`
    // gesetzt, aber `used_for_renewal_id` blieb `null` — der Filter darf sich
    // deshalb nicht ausschließlich auf `used_for_renewal_id` verlassen.
    const availableRecords = regularRecords.filter(r => !r.used_for_renewal_id && !r.used_for_license_id);
    // Nur durch die Geschäftsstelle anerkannte Records dürfen auf die UE-
    // Anforderung angerechnet werden. Submitted-Records werden separat als
    // "in Prüfung" ausgewiesen (transparenter Hinweis für das Mitglied).
    const approvedRecords = availableRecords.filter(r => (r.review_status || 'submitted') === 'approved');
    const pendingRecords = availableRecords.filter(r => (r.review_status || 'submitted') === 'submitted');
    const _ueOf = r => (r.approved_ue != null ? r.approved_ue : r.ue_count || 0);
    const availableUE = approvedRecords.reduce((sum, r) => sum + _ueOf(r), 0);
    const pendingUE = pendingRecords.reduce((sum, r) => sum + _ueOf(r), 0);
    const hasFirstAid = requirements?.has_first_aid ?? firstAidRecords.length > 0;
    const requiredUE = requirements?.required_ue ?? 0;
    const tierLabel = requirements?.tier_label || '';
    const renewable = requirements?.renewable ?? true;
    const ueReached = requiredUE > 0 ? availableUE >= requiredUE : availableUE > 0;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="license-renewal-page">
            <MemberHeader />
            <MemberBreadcrumb />

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Meine Lizenz verlängern</h2>
                        <p className="text-gray-500 mt-1 text-sm">
                            Reichen Sie Ihre Fortbildungsnachweise ein und beantragen Sie anschließend Ihre Lizenzverlängerung.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/portal')}
                        data-testid="back-to-portal-btn"
                    >
                        <ChevronLeft className="h-4 w-4 mr-1" /> Zurück zu „Mein Bereich"
                    </Button>
                </div>

                {profileIncomplete && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800" data-testid="profile-incomplete-hint">
                        <strong>Hinweis:</strong> Ihre Kontaktdaten sind unvollständig. Bitte
                        <Link to="/kontaktdaten" className="underline mx-1 font-medium">vervollständigen Sie Ihre Kontaktdaten</Link>
                        — diese erscheinen auf Ihrer Lizenzurkunde.
                    </div>
                )}

                {/* ===== Meine Lizenz (Upload-Gate oder Anzeige) ===== */}
                <section className="mb-8" data-testid="license-section">
                    <div className="flex items-center gap-2 mb-4">
                        <Shield className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold text-gray-900">Meine Lizenz</h3>
                    </div>

                    {licenses.length === 0 ? (
                        <Card className="border-2 border-dashed border-primary/40 bg-primary/5" data-testid="license-upload-gate">
                            <CardContent className="p-6 sm:p-7">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                                    <div className="p-3 bg-primary/10 rounded-xl shrink-0">
                                        <AlertCircle className="h-6 w-6 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base font-semibold text-gray-900 mb-1">
                                            Bitte laden Sie zuerst Ihre bestehende Lizenz hoch
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            Um eine Lizenzverlängerung beantragen zu können, muss Ihre aktuelle Lizenz in
                                            unserem System hinterlegt sein. Laden Sie Ihre bestehende Lizenz als
                                            <strong> PDF</strong> hoch und geben Sie Lizenznummer, Ausstellungs- und
                                            Gültigkeitsdatum an. Die Geschäftsstelle prüft und übernimmt anschließend Ihre Daten.
                                        </p>
                                    </div>
                                    <Button
                                        onClick={() => setUploadLicenseOpen(true)}
                                        className="bg-primary hover:bg-primary/80 shrink-0 whitespace-nowrap"
                                        data-testid="license-upload-gate-btn"
                                    >
                                        <Upload className="h-4 w-4 mr-1.5" />
                                        Lizenz hochladen
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        <LicensesGroupedList licenses={licenses} renewals={renewals} />
                    )}
                </section>

                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Linke Spalte: Einheitliche Nachweis-Liste */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* ===== Zusammengefasste Liste aller Fortbildungsnachweise ===== */}
                        <Card>
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <FileText className="h-4 w-4 text-primary" />
                                    <h3 className="text-sm font-semibold text-gray-900">Meine Fortbildungsnachweise</h3>
                                    <span className="text-xs text-gray-400 ml-auto">neueste zuerst</span>
                                </div>
                                {sortedRecords.length === 0 ? (
                                    <p className="text-sm text-gray-500 py-3">
                                        Es liegen noch keine Fortbildungsnachweise vor. Nutzen Sie die Upload-Felder rechts, um einen externen Nachweis oder eine externe Seminarbescheinigung einzureichen.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {sortedRecords.map((r) => {
                                            const isFirstAid = r.record_type === 'first_aid';
                                            const isSystem = !!r.auto_created;
                                            const status = r.review_status || 'submitted';
                                            const isApproved = isSystem || status === 'approved';
                                            const isRejected = status === 'rejected';
                                            const isPending = !isSystem && status === 'submitted';
                                            return (
                                                <div
                                                    key={r.id}
                                                    className={`flex items-start justify-between p-3 rounded-lg border ${
                                                        isRejected ? 'bg-red-50 border-red-200' :
                                                        r.used_for_renewal_id ? 'bg-gray-100 border-gray-200' :
                                                        'bg-white'
                                                    }`}
                                                    data-testid={`record-row-${r.id}`}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="text-sm font-medium text-gray-900 truncate">{r.name}</p>
                                                            {isFirstAid && (
                                                                <span className="text-[11px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                                                                    <HeartPulse className="h-3 w-3" /> Erste Hilfe
                                                                </span>
                                                            )}
                                                            {isSystem ? (
                                                                <span className="text-[11px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">Aus BGPR-Seminar</span>
                                                            ) : (
                                                                <span className="text-[11px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Extern hochgeladen</span>
                                                            )}
                                                            {isApproved && (
                                                                <span className="text-[11px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-1" data-testid={`badge-approved-${r.id}`}>
                                                                    <CheckCircle2 className="h-3 w-3" /> Anerkannt
                                                                </span>
                                                            )}
                                                            {isPending && (
                                                                <span className="text-[11px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid={`badge-pending-${r.id}`}>
                                                                    In Prüfung
                                                                </span>
                                                            )}
                                                            {isRejected && (
                                                                <span className="text-[11px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold" data-testid={`badge-rejected-${r.id}`}>
                                                                    Abgelehnt
                                                                </span>
                                                            )}
                                                            {(r.used_for_renewal_id || r.used_for_license_id) && (
                                                                <span className="text-[11px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title={r.used_at ? `Für Verlängerung verbraucht am ${formatDate(r.used_at?.split('T')[0])}` : 'Für Verlängerung verbraucht'}>
                                                                    {r.used_at ? `Verwendet am ${formatDate(r.used_at.split('T')[0])}` : 'Verwendet für Verlängerung'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                            Ausgestellt: {formatDate(r.date_issued)} &middot; {r.ue_count} UE
                                                            {r.approved_ue != null && r.approved_ue !== r.ue_count && !isRejected && (
                                                                <span className="text-green-700 font-semibold ml-1">(anerkannt: {r.approved_ue} UE)</span>
                                                            )}
                                                        </p>
                                                        {isRejected && r.rejection_reason && (
                                                            <p className="text-xs text-red-700 mt-1" data-testid={`rejection-reason-${r.id}`}>
                                                                <strong>Grund:</strong> {r.rejection_reason}
                                                            </p>
                                                        )}
                                                        {(r.document_path || (isSystem && r.booking_id)) && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDownloadRecord(r)}
                                                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                                                                data-testid={`download-record-${r.id}`}
                                                                title={r.document_path ? 'PDF öffnen' : 'Bescheinigung als PDF herunterladen (wird on-demand regeneriert)'}
                                                            >
                                                                <Download className="h-3 w-3" /> PDF herunterladen
                                                            </button>
                                                        )}
                                                    </div>
                                                    {!isSystem && !r.used_for_renewal_id && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-500 shrink-0"
                                                            onClick={() => handleDeleteRecord(r.id)}
                                                            data-testid={`delete-record-${r.id}`}
                                                            title="Nachweis löschen"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Rechte Spalte: Übersicht + Antrag + Upload-Formulare */}
                    <div className="space-y-6">
                        <Card>
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Shield className="h-4 w-4 text-primary" />
                                    <h3 className="text-sm font-semibold text-gray-900">Verlängerungs-Übersicht</h3>
                                </div>

                                <div className="p-3 bg-gray-50 rounded-xl border space-y-1.5 mb-4" data-testid="ue-overview">
                                    {requirements?.license ? (
                                        <div className="flex justify-between text-xs pb-1 border-b border-gray-200">
                                            <span className="text-gray-500">Lizenz gültig bis:</span>
                                            <span className="font-medium text-gray-900">
                                                {formatDate(requirements.license.valid_until)}
                                                {requirements.months_since_expiry > 0 && tierLabel
                                                    ? ` · ${tierLabel}`
                                                    : requirements.months_until_expiry > 0
                                                        ? ` · noch gültig`
                                                        : ''}
                                            </span>
                                        </div>
                                    ) : requirements?.needs_existing_license_upload ? (
                                        <div className="text-xs pb-1 border-b border-gray-200 text-amber-700">
                                            Noch keine Lizenz im System hinterlegt &ndash; bitte bei der Antragstellung Ihre bestehende Lizenz hochladen.
                                        </div>
                                    ) : null}
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Verfügbare Fortbildungs-UE:</span>
                                        <span className={`font-bold ${ueReached ? 'text-green-600' : 'text-amber-600'}`}>
                                            {availableUE} UE {requiredUE > 0 && <span className="font-normal text-gray-400">(erforderlich: {requiredUE})</span>}
                                        </span>
                                    </div>
                                    {requiredUE > 0 && (() => {
                                        const pct = Math.min(100, Math.round((availableUE / requiredUE) * 100));
                                        const barColor = ueReached ? 'bg-green-500' : pct >= 66 ? 'bg-amber-500' : 'bg-red-400';
                                        return (
                                            <div className="pt-1" data-testid="ue-progress">
                                                <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full ${barColor} transition-all duration-500`}
                                                        style={{ width: `${pct}%` }}
                                                        aria-valuenow={pct}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                        role="progressbar"
                                                    />
                                                </div>
                                                <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                                                    <span>{availableUE} von {requiredUE} UE gesammelt</span>
                                                    <span className={`font-semibold ${ueReached ? 'text-green-600' : 'text-amber-600'}`}>{pct}%</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {requiredUE > 0 && (
                                        <div className="flex justify-between text-xs">
                                            <span className="text-gray-500">Noch benötigt:</span>
                                            <span className={`font-medium ${availableUE >= requiredUE ? 'text-green-600' : 'text-amber-600'}`}>
                                                {Math.max(0, requiredUE - availableUE)} UE
                                            </span>
                                        </div>
                                    )}
                                    {pendingUE > 0 && (
                                        <div
                                            className="flex justify-between text-xs items-center bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1"
                                            data-testid="pending-review-ue-row"
                                        >
                                            <span className="text-amber-900">
                                                <strong>{pendingUE} UE</strong> in {pendingRecords.length === 1 ? 'einem Nachweis' : `${pendingRecords.length} Nachweisen`} warten auf Prüfung durch die Geschäftsstelle
                                            </span>
                                            <span className="text-amber-700 italic">noch nicht angerechnet</span>
                                        </div>
                                    )}
                                    {requiredUE > 0 && tierLabel && (
                                        <div className="text-[11px] text-gray-500 leading-relaxed pt-0.5">
                                            <span className="text-gray-400">Regel:</span>{' '}
                                            <span className="font-medium text-gray-700">„{tierLabel}"</span>{' '}
                                            <span className="text-gray-400">→ {requiredUE} UE erforderlich</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">1. Hilfe-Nachweis (HLW){requirements?.first_aid_max_age_years ? ` · max. ${requirements.first_aid_max_age_years} Jahre alt` : ''}:</span>
                                        {hasFirstAid
                                            ? <span className="text-green-600 font-semibold">Vorhanden</span>
                                            : <span className="text-red-500 font-semibold">Fehlt</span>
                                        }
                                    </div>
                                    {!renewable && requirements?.license && (
                                        <div className="text-xs text-red-600 font-medium pt-1">
                                            Lizenz zu lange abgelaufen &ndash; bitte wenden Sie sich an die Geschäftsstelle.
                                        </div>
                                    )}
                                </div>

                                {hasPendingRenewal ? (
                                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl" data-testid="renewal-pending">
                                        <Send className="h-5 w-5 text-blue-600 shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-blue-900">Antrag eingereicht</p>
                                            <p className="text-xs text-blue-700">Ihr Antrag wird von der Geschäftsstelle bearbeitet.</p>
                                        </div>
                                    </div>
                                ) : licenses.length === 0 ? (
                                    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="renewal-blocked-no-license">
                                        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                        <div className="text-xs text-amber-800">
                                            Bitte laden Sie zuerst Ihre bestehende Lizenz oben hoch, um einen Verlängerungs-Antrag stellen zu können.
                                        </div>
                                    </div>
                                ) : (!ueReached && !requirements?.half_extension_available) ? (
                                    <div className="space-y-2" data-testid="renewal-blocked-insufficient-ue">
                                        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
                                            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                                            <div className="text-xs text-red-800">
                                                <p className="font-semibold mb-0.5">UE-Anforderung noch nicht erfüllt</p>
                                                <p>
                                                    {tierLabel ? (
                                                        <>Entsprechend der Regel <strong>„{tierLabel}"</strong> (gemäß „UE-Anforderungen nach Lizenzstatus") benötigen Sie aktuell <strong>{requiredUE} UE</strong>. </>
                                                    ) : (
                                                        <>Für einen Antrag benötigen Sie <strong>{requiredUE} UE</strong>. </>
                                                    )}
                                                    Aktuell sind <strong>{availableUE} UE</strong> hinterlegt — es fehlen noch <strong>{Math.max(0, requiredUE - availableUE)} UE</strong>. Bitte reichen Sie zunächst weitere Fortbildungsnachweise ein.
                                                </p>
                                            </div>
                                        </div>
                                        <Button disabled className="w-full" data-testid="request-renewal-btn-disabled">
                                            <Send className="mr-2 h-4 w-4" /> Antrag (derzeit nicht möglich)
                                        </Button>
                                    </div>
                                ) : !hasFirstAid ? (
                                    <div className="space-y-2" data-testid="renewal-blocked-no-first-aid">
                                        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
                                            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                                            <div className="text-xs text-red-800">
                                                <p className="font-semibold mb-0.5">Erste-Hilfe-Nachweis fehlt</p>
                                                <p>
                                                    Für einen Antrag auf Lizenzverlängerung ist ein gültiger
                                                    Erste-Hilfe-Nachweis{requirements?.first_aid_max_age_years ? <> (max. <strong>{requirements.first_aid_max_age_years} Jahre alt</strong>)</> : ''} erforderlich.
                                                    Bitte laden Sie unten zunächst einen aktuellen Nachweis (z.&nbsp;B. 1st-Responder oder HLW-Auffrischung) hoch.
                                                </p>
                                            </div>
                                        </div>
                                        <Button disabled className="w-full" data-testid="request-renewal-btn-disabled-no-first-aid">
                                            <Send className="mr-2 h-4 w-4" /> Antrag (derzeit nicht möglich)
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        onClick={handleOpenRenewal}
                                        disabled={renewalSubmitting || !renewable}
                                        className="w-full bg-primary hover:bg-primary/80"
                                        data-testid="request-renewal-btn"
                                    >
                                        {renewalSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                                        Lizenzverlängerung beantragen
                                    </Button>
                                )}

                                {renewals.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        <p className="text-xs font-medium text-gray-500">Bisherige Anträge:</p>
                                        {renewals.map((r) => (
                                            <div key={r.id} className="flex items-center justify-between text-xs text-gray-500 p-2 bg-gray-50 rounded" data-testid={`renewal-${r.id}`}>
                                                <span>{formatDate(r.created_at?.split('T')[0])}</span>
                                                <span className={`px-2 py-0.5 rounded-full font-medium ${
                                                    r.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                    r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                    'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {r.status === 'approved' ? 'Genehmigt' : r.status === 'rejected' ? 'Abgelehnt' : 'In Bearbeitung'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* ===== Upload: Erste-Hilfe-Nachweis (externer Anbieter) ===== */}
                        <Card className="border-amber-200">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-1">
                                    <HeartPulse className="h-4 w-4 text-amber-600" />
                                    <h3 className="text-sm font-semibold text-gray-900">Erste-Hilfe-Nachweis hochladen</h3>
                                </div>
                                <p className="text-xs text-gray-500 mb-3">
                                    Z.&nbsp;B. 1st-Responder oder HLW-Auffrischung.
                                </p>
                                <div className="space-y-2">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Bezeichnung</Label>
                                        <Input
                                            value={newFirstAid.name}
                                            onChange={(e) => setNewFirstAid({ ...newFirstAid, name: e.target.value })}
                                            placeholder="z.B. 1st Responder (Johanniter)"
                                            className="h-9 text-sm"
                                            data-testid="first-aid-name"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Datum</Label>
                                            <Input type="date" value={newFirstAid.date_issued} onChange={(e) => setNewFirstAid({ ...newFirstAid, date_issued: e.target.value })} className="h-9 text-sm" data-testid="first-aid-date" />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">UE</Label>
                                            <Input type="number" min="1" value={newFirstAid.ue_count} onChange={(e) => setNewFirstAid({ ...newFirstAid, ue_count: e.target.value })} className="h-9 text-sm" data-testid="first-aid-ue" />
                                        </div>
                                    </div>
                                    <PdfDropzone
                                        file={firstAidFile}
                                        onChange={(f) => {
                                            setFirstAidFile(f);
                                            setFirstAidFileError(f ? validateFilenameForArchive(f.name) : '');
                                        }}
                                        label="PDF-Nachweis (optional)"
                                        hint="Datei hierhin ziehen oder klicken zum Auswählen."
                                        compact
                                        testId="first-aid-file"
                                        error={firstAidFileError}
                                    />
                                    <Button size="sm" onClick={() => handleAddRecord(newFirstAid, firstAidFile)} disabled={addingRecord || !!firstAidFileError} className="w-full h-9" data-testid="add-first-aid-btn">
                                        <Plus className="h-4 w-4 mr-1" /> Hinzufügen
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* ===== Upload: Externe Seminarbescheinigung ===== */}
                        <Card>
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-1">
                                    <FileText className="h-4 w-4 text-primary" />
                                    <h3 className="text-sm font-semibold text-gray-900">Externe Seminarbescheinigung</h3>
                                </div>
                                <p className="text-xs text-gray-500 mb-3">
                                    Fortbildungen anderer Anbieter zur UE-Anrechnung.
                                </p>
                                <div className="space-y-2">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Bezeichnung der Fortbildung</Label>
                                        <Input
                                            value={newRecord.name}
                                            onChange={(e) => setNewRecord({ ...newRecord, name: e.target.value })}
                                            placeholder="z.B. Auffrischungskurs"
                                            className="h-9 text-sm"
                                            data-testid="record-name"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Datum</Label>
                                            <Input type="date" value={newRecord.date_issued} onChange={(e) => setNewRecord({ ...newRecord, date_issued: e.target.value })} className="h-9 text-sm" data-testid="record-date" />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">UE</Label>
                                            <Input type="number" min="1" value={newRecord.ue_count} onChange={(e) => setNewRecord({ ...newRecord, ue_count: e.target.value })} className="h-9 text-sm" data-testid="record-ue" />
                                        </div>
                                    </div>
                                    <PdfDropzone
                                        file={recordFile}
                                        onChange={(f) => {
                                            setRecordFile(f);
                                            setRecordFileError(f ? validateFilenameForArchive(f.name) : '');
                                        }}
                                        label="PDF-Nachweis (optional, max. 10 MB)"
                                        hint="Datei hierhin ziehen oder klicken zum Auswählen."
                                        compact
                                        testId="record-file"
                                        error={recordFileError}
                                    />
                                    <Button size="sm" onClick={() => handleAddRecord(newRecord, recordFile)} disabled={addingRecord || !!recordFileError} className="w-full h-9" data-testid="add-record-btn">
                                        <Plus className="h-4 w-4 mr-1" /> Hinzufügen
                                    </Button>

                                    {/* Stapel-Upload für mehrere Nachweise auf einmal */}
                                    <button
                                        type="button"
                                        onClick={() => setBatchOpen((v) => !v)}
                                        className="w-full text-xs text-primary hover:text-primary/80 flex items-center justify-center gap-1.5 py-1.5 mt-1"
                                        data-testid="batch-toggle"
                                    >
                                        <Layers className="h-3.5 w-3.5" />
                                        Mehrere Nachweise auf einmal hochladen
                                        {batchOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                    </button>

                                    {batchOpen && (
                                        <div className="space-y-3 pt-2 border-t" data-testid="batch-section">
                                            <PdfDropzone
                                                file={null}
                                                onChange={(files) => { if (files?.length) handleAddBatchFiles(files); }}
                                                label="Mehrere PDFs ablegen"
                                                hint="Mehrere PDFs gleichzeitig hier hineinziehen — pro Datei werden Datum & UE einzeln eingetragen."
                                                compact
                                                multiple
                                                testId="batch-dropzone"
                                            />

                                            {batchRecords.length > 0 && (
                                                <div className="space-y-2">
                                                    {batchRecords.map((r) => (
                                                        <div
                                                            key={r.id}
                                                            className={`rounded-lg border p-2.5 space-y-1.5 ${r.status === 'error' ? 'border-rose-300 bg-rose-50/40' : 'border-gray-200 bg-white'}`}
                                                            data-testid={`batch-row-${r.id}`}
                                                        >
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex items-center gap-1.5 min-w-0 text-xs">
                                                                    <FileText className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                                                                    <span className="truncate font-medium" title={r.file.name}>{r.file.name}</span>
                                                                    {r.status === 'error' && <AlertCircle className="h-3.5 w-3.5 text-rose-600 flex-shrink-0" />}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeBatchRow(r.id)}
                                                                    disabled={batchSubmitting}
                                                                    className="text-rose-600 hover:text-rose-700 flex-shrink-0"
                                                                    aria-label="Zeile entfernen"
                                                                >
                                                                    <XIcon className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>
                                                            <Input
                                                                value={r.name}
                                                                onChange={(e) => updateBatchRow(r.id, { name: e.target.value })}
                                                                placeholder="Bezeichnung der Fortbildung"
                                                                className="h-8 text-xs"
                                                                disabled={batchSubmitting}
                                                                data-testid={`batch-name-${r.id}`}
                                                            />
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <Input
                                                                    type="date"
                                                                    value={r.date_issued}
                                                                    onChange={(e) => updateBatchRow(r.id, { date_issued: e.target.value })}
                                                                    className="h-8 text-xs"
                                                                    disabled={batchSubmitting}
                                                                    data-testid={`batch-date-${r.id}`}
                                                                />
                                                                <Input
                                                                    type="number"
                                                                    min="1"
                                                                    value={r.ue_count}
                                                                    onChange={(e) => updateBatchRow(r.id, { ue_count: e.target.value })}
                                                                    placeholder="UE"
                                                                    className="h-8 text-xs"
                                                                    disabled={batchSubmitting}
                                                                    data-testid={`batch-ue-${r.id}`}
                                                                />
                                                            </div>
                                                            {r.error && (
                                                                <p className="text-[11px] text-rose-700">{r.error}</p>
                                                            )}
                                                            {r.status === 'error' && r.errorMsg && (
                                                                <p className="text-[11px] text-rose-700">{r.errorMsg}</p>
                                                            )}
                                                        </div>
                                                    ))}

                                                    <Button
                                                        size="sm"
                                                        onClick={handleSubmitBatch}
                                                        disabled={batchSubmitting || batchRecords.length === 0}
                                                        className="w-full h-9"
                                                        data-testid="batch-submit"
                                                    >
                                                        {batchSubmitting ? (
                                                            <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Lade {batchProgress.done}/{batchProgress.total}…</>
                                                        ) : (
                                                            <><Upload className="h-4 w-4 mr-1.5" /> Alle {batchRecords.length} Nachweise hinzufügen</>
                                                        )}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {loading && (
                    <div className="flex justify-center py-12" data-testid="loading-spinner">
                        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
                    </div>
                )}
            </main>

            {/* Renewal dialog */}
            <Dialog open={renewalDialogOpen} onOpenChange={setRenewalDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="renewal-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Lizenzverlängerung beantragen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {requirements?.needs_existing_license_upload && (
                            <div className="space-y-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                                <p className="text-sm text-amber-900">
                                    Für Sie ist noch keine Lizenz im System hinterlegt.
                                    Bitte laden Sie Ihre bestehende Lizenz hoch und geben Sie die Eckdaten an.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1 sm:col-span-2">
                                        <Label className="text-xs">Lizenznummer <span className="text-red-500">*</span></Label>
                                        <Input value={renewalForm.license_number} onChange={(e) => setRenewalForm({ ...renewalForm, license_number: e.target.value })} placeholder="z.B. BGPR-HGL-2020-012" data-testid="renewal-license-number" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Ausstellungsdatum <span className="text-red-500">*</span></Label>
                                        <Input type="date" min="1900-01-01" max="2100-12-31" value={renewalForm.issued_date} onChange={(e) => setRenewalForm({ ...renewalForm, issued_date: e.target.value })} data-testid="renewal-issued-date" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Gültig bis <span className="text-red-500">*</span></Label>
                                        <Input type="date" min="1900-01-01" max="2100-12-31" value={renewalForm.valid_until} onChange={(e) => setRenewalForm({ ...renewalForm, valid_until: e.target.value })} data-testid="renewal-valid-until" />
                                    </div>
                                    <div className="space-y-1 sm:col-span-2">
                                        <PdfDropzone
                                            file={renewalForm.file}
                                            onChange={(f) => setRenewalForm({ ...renewalForm, file: f })}
                                            label="Lizenz-PDF *"
                                            hint="Nur PDF, max. 10 MB · Datei hierhin ziehen oder klicken."
                                            testId="renewal-license-file"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="space-y-1">
                            <Label className="text-xs">Anmerkung (optional)</Label>
                            <Input value={renewalForm.notes} onChange={(e) => setRenewalForm({ ...renewalForm, notes: e.target.value })} placeholder="Optionale Mitteilung an die Geschäftsstelle" data-testid="renewal-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenewalDialogOpen(false)}>Abbrechen</Button>
                        <Button onClick={handleSubmitRenewal} disabled={renewalSubmitting} data-testid="renewal-submit">
                            {renewalSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                            Antrag einreichen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Self-upload existing license dialog */}
            <Dialog open={uploadLicenseOpen} onOpenChange={setUploadLicenseOpen}>
                <DialogContent className="max-w-lg" data-testid="upload-license-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Bestehende Lizenz hochladen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Laden Sie Ihre aktuelle Herzgruppenleiter-Lizenz als PDF hoch. Die Geschäftsstelle
                            prüft die Daten und übernimmt die Lizenz in Ihr Profil. Anschließend können Sie
                            die Verlängerung beantragen.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1 sm:col-span-2">
                                <Label className="text-xs">Lizenznummer <span className="text-red-500">*</span></Label>
                                <Input value={uploadLicenseForm.license_number} onChange={(e) => setUploadLicenseForm({ ...uploadLicenseForm, license_number: e.target.value })} placeholder="z.B. BGPR-HGL-2020-012" data-testid="upload-lic-number" />
                            </div>
                            <div className="space-y-1 sm:col-span-2">
                                <Label className="text-xs">Lizenztyp <span className="text-red-500">*</span></Label>
                                <Select value={uploadLicenseForm.license_type} onValueChange={(v) => setUploadLicenseForm({ ...uploadLicenseForm, license_type: v })}>
                                    <SelectTrigger data-testid="upload-lic-type"><SelectValue placeholder="Bitte wählen..." /></SelectTrigger>
                                    <SelectContent>
                                        {licenseTypeOptions.length === 0 ? (
                                            <SelectItem value="__none" disabled>Keine Lizenztypen konfiguriert</SelectItem>
                                        ) : licenseTypeOptions.map((opt) => (
                                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Ausstellungsdatum <span className="text-red-500">*</span></Label>
                                <Input type="date" min="1900-01-01" max="2100-12-31" value={uploadLicenseForm.issued_date} onChange={(e) => setUploadLicenseForm({ ...uploadLicenseForm, issued_date: e.target.value })} data-testid="upload-lic-issued" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Gültig bis <span className="text-red-500">*</span></Label>
                                <Input type="date" min="1900-01-01" max="2100-12-31" value={uploadLicenseForm.valid_until} onChange={(e) => setUploadLicenseForm({ ...uploadLicenseForm, valid_until: e.target.value })} data-testid="upload-lic-valid-until" />
                            </div>
                            <div className="space-y-1 sm:col-span-2">
                                <Label className="text-xs">Erstausstellungsdatum (optional)</Label>
                                <Input type="date" min="1900-01-01" max="2100-12-31" value={uploadLicenseForm.original_issued_date} onChange={(e) => setUploadLicenseForm({ ...uploadLicenseForm, original_issued_date: e.target.value })} data-testid="upload-lic-original-issued" />
                                <p className="text-xs text-muted-foreground">Datum, an dem Ihre Lizenz erstmals ausgestellt wurde.</p>
                            </div>
                            <div className="space-y-1 sm:col-span-2">
                                <PdfDropzone
                                    file={uploadLicenseForm.file}
                                    onChange={(f) => setUploadLicenseForm({ ...uploadLicenseForm, file: f })}
                                    label="Lizenz-PDF *"
                                    hint="Nur PDF, max. 10 MB · Datei hierhin ziehen oder klicken."
                                    testId="upload-lic-file"
                                    error={uploadLicenseForm.file ? validateFilenameForArchive(uploadLicenseForm.file.name) : ''}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUploadLicenseOpen(false)}>Abbrechen</Button>
                        <Button
                            onClick={handleSelfUploadLicense}
                            disabled={uploadingLicense || !!(uploadLicenseForm.file && validateFilenameForArchive(uploadLicenseForm.file.name))}
                            data-testid="upload-lic-submit"
                        >
                            {uploadingLicense ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Lizenz speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
